import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { 
  Camera, 
  CameraOff, 
  Mic, 
  MicOff, 
  Phone,
  PhoneOff,
  Heart,
  X,
  Clock,
  Users,
  MessageSquare,
  Home
} from 'lucide-react';
import axios from 'axios';
import VideoService from '../services/VideoService';
import UpgradeModal from './UpgradeModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const RealSpeedDatingSession = () => {
  const navigate = useNavigate();
  
  // Session states
  const [sessionState, setSessionState] = useState('loading'); // loading, queuing, matched, in_session, ended
  const [currentSession, setCurrentSession] = useState(null);
  const [partner, setPartner] = useState(null);
  const [timeRemaining, setTimeRemaining] = useState(180); // 3 minutes
  
  // UI states
  const [cameraEnabled, setCameraEnabled] = useState(true);
  const [audioEnabled, setAudioEnabled] = useState(true);
  const [hasDecided, setHasDecided] = useState(false);
  const [decision, setDecision] = useState(null);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [userTier, setUserTier] = useState('free');
  
  // Refs
  const localVideoRef = useRef(null);
  const remoteVideoRef = useRef(null);
  const intervalRef = useRef(null);
  
  // Video connection
  const [videoConnected, setVideoConnected] = useState(false);
  
  // Icebreakers
  const [icebreakers] = useState([
    "What's the most adventurous thing you've done recently?",
    "If you could have dinner with anyone, who would it be?",
    "What's your favorite way to spend a weekend?",
    "What's something you're passionate about?",
    "If you could travel anywhere right now, where would you go?"
  ]);
  const [currentIcebreaker, setCurrentIcebreaker] = useState(0);

  // Initialize session when component mounts
  useEffect(() => {
    initializeSession();
    fetchUserTier();
    return () => {
      // Cleanup on unmount - stop all cameras
      console.log('🧹 Component unmounting, cleaning up cameras...');
      
      if (localVideoRef.current && localVideoRef.current.srcObject) {
        const stream = localVideoRef.current.srcObject;
        stream.getTracks().forEach(track => track.stop());
        localVideoRef.current.srcObject = null;
      }
      
      if (remoteVideoRef.current && remoteVideoRef.current.srcObject) {
        const stream = remoteVideoRef.current.srcObject;
        stream.getTracks().forEach(track => track.stop());
        remoteVideoRef.current.srcObject = null;
      }
      
      VideoService.disconnect();
      
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  // Timer effect
  useEffect(() => {
    if (sessionState === 'in_session' && timeRemaining > 0) {
      intervalRef.current = setInterval(() => {
        setTimeRemaining(prev => {
          if (prev <= 1) {
            // Immediately stop camera when timer expires
            console.log('⏰ Timer expired - force stopping camera NOW');
            if (localVideoRef.current?.srcObject) {
              localVideoRef.current.srcObject.getTracks().forEach(track => {
                console.log('Stopping track immediately:', track.kind);
                track.stop();
              });
              localVideoRef.current.srcObject = null;
            }
            if (remoteVideoRef.current?.srcObject) {
              remoteVideoRef.current.srcObject.getTracks().forEach(track => track.stop());
              remoteVideoRef.current.srcObject = null;
            }
            VideoService.disconnect();
            
            // Then end the session
            endSession();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } else {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    }
    
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [sessionState, timeRemaining]);



  const fetchUserTier = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/subscription/status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.data) {
        const data = response.data;
        if (data.is_vip) {
          setUserTier('vip');
        } else if (data.is_premium) {
          setUserTier('premium');
        } else {
          setUserTier('free');
        }
        console.log('📊 User tier:', data.is_vip ? 'vip' : data.is_premium ? 'premium' : 'free');
      }
    } catch (error) {
      console.error('Error fetching user tier:', error);
      setUserTier('free'); // Default to free on error
    }
  };

  const initializeSession = async () => {
    try {
      setSessionState('loading');
      
      const token = localStorage.getItem('token');
      if (!token) {
        console.error('No auth token found');
        navigate('/login');
        return;
      }
      
      // Check if there's a specific session to join from URL parameters
      const urlParams = new URLSearchParams(window.location.search);
      const joinSessionId = urlParams.get('join');
      
      if (joinSessionId) {
        // User clicked on notification to join specific session
        console.log('Joining specific session from notification:', joinSessionId);
        await joinSpecificSession(joinSessionId);
        return;
      }
      
      // Check if user has an active session first
      const activeResponse = await axios.get(`${API}/sessions/active`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (activeResponse.data.session) {
        // User has an active session, join it
        console.log('Found active session:', activeResponse.data.session);
        setCurrentSession(activeResponse.data.session);
        setPartner(activeResponse.data.session.partner_info);
        setSessionState('in_session');
        setTimeRemaining(activeResponse.data.session.time_remaining || 180);
        // Connect to video
        await connectToVideo(activeResponse.data.session);
      } else {
        // No active session, join the queue
        console.log('No active session, joining queue');
        await joinQueue();
      }
    } catch (error) {
      console.error('Error initializing session:', error);
      // If no active session, join queue
      await joinQueue();
    }
  };

  const joinSpecificSession = async (sessionId) => {
    try {
      setSessionState('loading');
      
      const token = localStorage.getItem('token');
      if (!token) {
        console.error('No auth token found for joining specific session');
        navigate('/login');
        return;
      }
      
      console.log('Attempting to join specific session:', sessionId);
      const response = await axios.post(`${API}/sessions/${sessionId}/join`, {}, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      console.log('Join specific session response:', response.data);
      
      if (response.data.status === 'active') {
        console.log('Successfully joined! Setting up active session...');
        setCurrentSession(response.data);
        setPartner(response.data.partner_info);
        setSessionState('in_session');
        setTimeRemaining(response.data.time_remaining || 180);
        // Connect to video
        await connectToVideo(response.data);
        
        // Clear URL parameters
        window.history.replaceState({}, document.title, window.location.pathname);
      } else if (response.data.status === 'waiting') {
        // Session is still waiting, start polling
        console.log('Session still waiting, starting to poll...');
        setCurrentSession(response.data);
        setSessionState('queuing');
        pollForMatch(response.data.session_id);
      }
    } catch (error) {
      console.error('Error joining specific session:', error);
      setSessionState('error');
      // Clear URL parameters on error
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  };

  const joinQueue = async () => {
    try {
      setSessionState('queuing');
      
      const token = localStorage.getItem('token');
      if (!token) {
        console.error('No auth token found for joining queue');
        navigate('/login');
        return;
      }
      
      console.log('Joining speed dating queue...');
      const response = await axios.post(`${API}/sessions/join`, {
        preferences: {
          age_range: [18, 100],
          distance_miles: 50
        }
      }, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      console.log('Queue join response:', response.data);
      
      if (response.data.status === 'active') {
        console.log('Immediately matched! Setting up session...');
        setCurrentSession(response.data);
        setPartner(response.data.partner_info);
        setSessionState('in_session');
        setTimeRemaining(response.data.time_remaining || 180);
        // Connect to video
        await connectToVideo(response.data);
      } else {
        // Still waiting for a match
        console.log('Waiting for match, starting to poll session:', response.data.session_id);
        pollForMatch(response.data.session_id);
      }
    } catch (error) {
      console.error('Error joining queue:', error);
      setSessionState('error');
    }
  };

  const pollForMatch = async (sessionId) => {
    const token = localStorage.getItem('token');
    if (!token) {
      console.error('No auth token for polling');
      return;
    }
    
    console.log('Starting to poll for match on session:', sessionId);
    
    const pollInterval = setInterval(async () => {
      try {
        console.log('Polling session status...');
        const response = await axios.get(`${API}/sessions/${sessionId}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        console.log('Poll response:', response.data);
        
        if (response.data.status === 'active') {
          console.log('Match found! Transitioning to in_session');
          clearInterval(pollInterval);
          setCurrentSession(response.data);
          setPartner(response.data.partner_info);
          setSessionState('in_session');
          setTimeRemaining(response.data.time_remaining || 180);
          // Connect to video
          await connectToVideo(response.data);
        }
      } catch (error) {
        console.error('Error polling for match:', error);
        clearInterval(pollInterval);
        setSessionState('error');
      }
    }, 3000); // Poll every 3 seconds

    // Stop polling after 2 minutes if no match
    setTimeout(() => {
      clearInterval(pollInterval);
      if (sessionState === 'queuing') {
        console.log('Polling timeout - no match found');
        setSessionState('no_match');
      }
    }, 120000);
  };

  const connectToVideo = async (session) => {
    try {
      if (!session.room_name || !session.twilio_token) {
        console.error('Missing room name or Twilio token');
        return;
      }

      // Initialize video service (it's a singleton)
      await VideoService.initialize();

      // Attach local video tracks
      const localTracks = VideoService.localTracks;
      if (localTracks && localVideoRef.current) {
        const videoTrack = localTracks.find(track => track.kind === 'video');
        if (videoTrack) {
          videoTrack.attach(localVideoRef.current);
        }
      }

      // Listen for track subscriptions (remote participants)
      VideoService.on('trackSubscribed', ({ track, participant }) => {
        if (track.kind === 'video' && remoteVideoRef.current) {
          track.attach(remoteVideoRef.current);
        }
      });

      // Connect to the video room
      const userId = localStorage.getItem('user_id') || 'user';
      await VideoService.connectToRoom(session.room_name, userId);

      setVideoConnected(true);
      console.log('Video connection established');
    } catch (error) {
      console.error('Error connecting to video:', error);
    }
  };

  const endSession = async () => {
    try {
      console.log('🛑 Ending session and disconnecting camera...');
      
      // Stop all video tracks first
      if (localVideoRef.current && localVideoRef.current.srcObject) {
        const stream = localVideoRef.current.srcObject;
        stream.getTracks().forEach(track => {
          console.log('Stopping track:', track.kind);
          track.stop();
        });
        localVideoRef.current.srcObject = null;
      }
      
      // Disconnect from VideoService
      VideoService.disconnect();
      
      const token = localStorage.getItem('token');
      if (currentSession?.session_id && token) {
        await axios.post(`${API}/sessions/${currentSession.session_id}/end`, {}, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
      }
      setSessionState('ended');
      setVideoConnected(false);
      console.log('✅ Session ended, camera stopped');
    } catch (error) {
      console.error('Error ending session:', error);
    }
  };



  const ensureCameraStopped = () => {
    console.log('🔒 Ensuring all cameras are stopped...');
    
    // Stop local video
    if (localVideoRef.current && localVideoRef.current.srcObject) {
      const stream = localVideoRef.current.srcObject;
      stream.getTracks().forEach(track => {
        console.log('Force stopping track:', track.kind);
        track.stop();
      });
      localVideoRef.current.srcObject = null;
    }
    
    // Stop remote video
    if (remoteVideoRef.current && remoteVideoRef.current.srcObject) {
      const stream = remoteVideoRef.current.srcObject;
      stream.getTracks().forEach(track => track.stop());
      remoteVideoRef.current.srcObject = null;
    }
    
    // Disconnect VideoService
    VideoService.disconnect();
    console.log('✅ All cameras stopped');
  };

  const handleNavigateToMatches = () => {
    ensureCameraStopped();
    navigate('/matches');
  };

  const handleStartAnother = () => {
    ensureCameraStopped();
    window.location.reload();
  };

  const handleUpgradeModalClose = () => {
    ensureCameraStopped();
    setShowUpgradeModal(false);
  };

  const recordDecision = async (liked) => {
    try {
      if (currentSession?.session_id) {
        await axios.post(`${API}/sessions/${currentSession.session_id}/decision`, {
          liked: liked
        });
        setDecision(liked);
        setHasDecided(true);
      }
    } catch (error) {
      console.error('Error recording decision:', error);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const nextIcebreaker = () => {
    setCurrentIcebreaker((prev) => (prev + 1) % icebreakers.length);
  };

  const renderLoadingState = () => (
    <div className="min-h-screen bg-gradient-to-br from-rose-900 via-slate-900 to-indigo-900 flex items-center justify-center p-4 overflow-x-hidden">
      <Card className="w-full max-w-md backdrop-blur-xl bg-black/20 border border-white/20">
        <CardContent className="text-center py-12">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-rose-500 border-t-transparent mx-auto mb-6"></div>
          <h2 className="text-2xl font-bold text-white mb-4">Initializing Speed Dating...</h2>
          <p className="text-gray-300">Setting up your session</p>
        </CardContent>
      </Card>
    </div>
  );

  const renderQueuingState = () => (
    <div className="min-h-screen bg-gradient-to-br from-rose-900 via-slate-900 to-indigo-900 flex items-center justify-center p-4 overflow-x-hidden">
      <Card className="w-full max-w-md backdrop-blur-xl bg-black/20 border border-white/20">
        <CardContent className="text-center py-12">
          <Users className="h-16 w-16 text-rose-500 mx-auto mb-6" />
          <h2 className="text-2xl font-bold text-white mb-4">Finding Your Match...</h2>
          <p className="text-gray-300 mb-6">We're looking for someone perfect for you</p>
          <div className="flex justify-center space-x-2 mb-6">
            <div className="w-3 h-3 bg-rose-500 rounded-full animate-bounce"></div>
            <div className="w-3 h-3 bg-rose-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
            <div className="w-3 h-3 bg-rose-300 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
          </div>
          <Button 
            onClick={() => navigate('/matches')}
            variant="outline" 
            className="text-white border-white/20 hover:bg-white/10"
          >
            Cancel and Browse Matches
          </Button>
        </CardContent>
      </Card>
    </div>
  );

  const renderSpeedDatingSession = () => (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 p-4">
      <div className="max-w-6xl mx-auto">
        {/* Header with timer and controls */}
        <div className="flex justify-between items-center mb-6">
          <Button
            onClick={() => navigate('/matches')}
            variant="outline"
            className="text-white border-white/20 hover:bg-white/10"
          >
            <Home className="h-4 w-4 mr-2" />
            Back to Home
          </Button>
          
          <div className="text-center">
            <div className="text-4xl font-bold text-white mb-2">
              <Clock className="inline h-8 w-8 mr-2 text-rose-500" />
              {formatTime(timeRemaining)}
            </div>
            <Badge variant="secondary" className="bg-rose-500/20 text-rose-300">
              Speed Dating Session
            </Badge>
          </div>

          <div className="flex space-x-2">
            <Button
              onClick={() => setCameraEnabled(!cameraEnabled)}
              variant={cameraEnabled ? "default" : "destructive"}
              size="sm"
            >
              {cameraEnabled ? <Camera className="h-4 w-4" /> : <CameraOff className="h-4 w-4" />}
            </Button>
            <Button
              onClick={() => setAudioEnabled(!audioEnabled)}
              variant={audioEnabled ? "default" : "destructive"}
              size="sm"
            >
              {audioEnabled ? <Mic className="h-4 w-4" /> : <MicOff className="h-4 w-4" />}
            </Button>
            <Button
              onClick={endSession}
              variant="destructive"
              size="sm"
            >
              <PhoneOff className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Video chat area */}
        <div className="grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            <Card className="bg-black/20 border border-white/20">
              <CardContent className="p-4">
                <div className="grid md:grid-cols-2 gap-4 h-96">
                  {/* Local video */}
                  <div className="relative bg-gray-900 rounded-lg overflow-hidden">
                    <video
                      ref={localVideoRef}
                      autoPlay
                      muted
                      playsInline
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute bottom-2 left-2 bg-black/50 text-white px-2 py-1 rounded text-sm">
                      You
                    </div>
                  </div>
                  
                  {/* Remote video or partner info */}
                  <div className="relative bg-gray-900 rounded-lg overflow-hidden">
                    {partner ? (
                      <>
                        <video
                          ref={remoteVideoRef}
                          autoPlay
                          playsInline
                          className="w-full h-full object-cover"
                        />
                        <div className="absolute bottom-2 left-2 bg-black/50 text-white px-2 py-1 rounded text-sm">
                          {partner.name}
                        </div>
                      </>
                    ) : (
                      <div className="w-full h-full flex flex-col items-center justify-center bg-gradient-to-br from-rose-500 to-pink-500">
                        <Users className="h-16 w-16 text-white/80 mb-4" />
                        <p className="text-white text-center">
                          Connecting to your match...
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Icebreakers and controls */}
          <div className="space-y-4">
            <Card className="bg-black/20 border border-white/20">
              <CardHeader>
                <CardTitle className="text-white flex items-center">
                  <MessageSquare className="h-5 w-5 mr-2 text-rose-500" />
                  Icebreaker
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="bg-rose-500/10 rounded-lg p-4 mb-4">
                  <p className="text-white text-lg font-medium">
                    {icebreakers[currentIcebreaker]}
                  </p>
                </div>
                <Button
                  onClick={nextIcebreaker}
                  variant="outline"
                  className="w-full text-white border-white/20 hover:bg-white/10"
                >
                  Next Question
                </Button>
              </CardContent>
            </Card>

            {/* Decision buttons */}
            {!hasDecided && timeRemaining <= 30 && (
              <Card className="bg-black/20 border border-white/20">
                <CardHeader>
                  <CardTitle className="text-white">How did it go?</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <Button
                    onClick={() => recordDecision(true)}
                    className="w-full bg-green-600 hover:bg-green-700 text-white"
                  >
                    <Heart className="h-4 w-4 mr-2" />
                    I'd like to match!
                  </Button>
                  <Button
                    onClick={() => recordDecision(false)}
                    variant="outline"
                    className="w-full text-white border-white/20 hover:bg-white/10"
                  >
                    <X className="h-4 w-4 mr-2" />
                    Not a match
                  </Button>
                </CardContent>
              </Card>
            )}

            {hasDecided && (
              <Card className="bg-black/20 border border-white/20">
                <CardContent className="text-center py-6">
                  <div className="text-green-400 text-2xl mb-2">
                    {decision ? '💚' : '🤝'}
                  </div>
                  <p className="text-white">
                    {decision ? "Great choice! We'll let you know if they're interested too." : "Thanks for your honesty. Keep looking for your perfect match!"}
                  </p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  const renderEndedState = () => {
    // Ensure camera is stopped when showing ended state
    useEffect(() => {
      console.log('📹 Ended state rendered - ensuring camera is OFF');
      if (localVideoRef.current?.srcObject) {
        console.log('⚠️ Camera still on in ended state - forcing stop');
        localVideoRef.current.srcObject.getTracks().forEach(track => track.stop());
        localVideoRef.current.srcObject = null;
      }
      if (remoteVideoRef.current?.srcObject) {
        remoteVideoRef.current.srcObject.getTracks().forEach(track => track.stop());
        remoteVideoRef.current.srcObject = null;
      }
      
      // Automatically show upgrade modal for free users when session ends
      if (userTier === 'free' && sessionState === 'ended') {
        console.log('🔔 Free user - showing upgrade modal after speed date');
        setShowUpgradeModal(true);
      }
    }, [sessionState]);
    
    return (
      <div className="min-h-screen bg-gradient-to-br from-rose-900 via-slate-900 to-indigo-900 flex items-center justify-center p-4 overflow-x-hidden">
        <Card className="w-full max-w-md backdrop-blur-xl bg-black/20 border border-white/20">
          <CardContent className="text-center py-12">
            <Heart className="h-16 w-16 text-rose-500 mx-auto mb-6" />
            <h2 className="text-2xl font-bold text-white mb-4">Session Complete!</h2>
            <p className="text-gray-300 mb-6">
              {decision ? "We'll notify you if it's a mutual match!" : "Thanks for participating. Keep dating!"}
            </p>
            <div className="space-y-3">
              <Button
                onClick={handleNavigateToMatches}
                className="w-full bg-rose-600 hover:bg-rose-700"
              >
                Find More Matches
              </Button>
              <Button
                onClick={handleStartAnother}
                variant="outline"
                className="w-full text-white border-white/20 hover:bg-white/10"
              >
                Start Another Session
              </Button>
            </div>
          </CardContent>
        </Card>
        
        {/* Upgrade Modal */}
        {showUpgradeModal && (
          <div className="fixed inset-0 z-[9999]">
            <UpgradeModal
              isOpen={showUpgradeModal}
              onClose={handleUpgradeModalClose}
              feature="session_complete"
            />
          </div>
        )}
      </div>
    );
  };

  // Render different states
  switch (sessionState) {
    case 'loading':
      return renderLoadingState();
    case 'queuing':
      return renderQueuingState();
    case 'in_session':
      return renderSpeedDatingSession();
    case 'ended':
      return renderEndedState();
    case 'no_match':
      return (
        <div className="min-h-screen bg-gradient-to-br from-rose-900 via-slate-900 to-indigo-900 flex items-center justify-center p-4 overflow-x-hidden">
          <Card className="w-full max-w-md backdrop-blur-xl bg-black/20 border border-white/20">
            <CardContent className="text-center py-12">
              <Users className="h-16 w-16 text-gray-400 mx-auto mb-6" />
              <h2 className="text-2xl font-bold text-white mb-4">No matches found</h2>
              <p className="text-gray-300 mb-6">Try again later or browse existing matches</p>
              <Button
                onClick={() => navigate('/matches')}
                className="w-full bg-rose-600 hover:bg-rose-700"
              >
                Browse Matches
              </Button>
            </CardContent>
          </Card>
        </div>
      );
    case 'error':
    default:
      return (
        <div className="min-h-screen bg-gradient-to-br from-rose-900 via-slate-900 to-indigo-900 flex items-center justify-center p-4 overflow-x-hidden">
          <Card className="w-full max-w-md backdrop-blur-xl bg-black/20 border border-white/20">
            <CardContent className="text-center py-12">
              <X className="h-16 w-16 text-red-500 mx-auto mb-6" />
              <h2 className="text-2xl font-bold text-white mb-4">Connection Error</h2>
              <p className="text-gray-300 mb-6">Unable to start speed dating session</p>
              <Button
                onClick={() => window.location.reload()}
                className="w-full bg-rose-600 hover:bg-rose-700"
              >
                Try Again
              </Button>
            </CardContent>
          </Card>
        </div>
      );
  }
};

export default RealSpeedDatingSession;